/**
 * System limits and constraints
 */

// User Limits
export const USER_LIMITS = {
  DISPLAY_NAME_MIN_LENGTH: 2,
  DISPLAY_NAME_MAX_LENGTH: 100,
  BIO_MAX_LENGTH: 500,
  INSTITUTION_MAX_LENGTH: 200,
} as const

// Course Limits
export const COURSE_LIMITS = {
  NAME_MIN_LENGTH: 3,
  NAME_MAX_LENGTH: 200,
  CODE_MIN_LENGTH: 3,
  CODE_MAX_LENGTH: 50,
  DESCRIPTION_MAX_LENGTH: 1000,
  MAX_COURSES_PER_PROFESSOR: 50, // Free plan limit
} as const

// Question Limits
export const QUESTION_LIMITS = {
  STATEMENT_MIN_LENGTH: 10,
  STATEMENT_MAX_LENGTH: 2000,
  POINTS_MIN: 0.1,
  POINTS_MAX: 100,
  MAX_POINTS_DECIMALS: 2,
  HINT_MAX_LENGTH: 500,
  FEEDBACK_MAX_LENGTH: 1000,

  // Multiple Choice
  MIN_OPTIONS: 2,
  MAX_OPTIONS: 10,
  OPTION_TEXT_MAX_LENGTH: 500,

  // Fill Blank
  MAX_BLANKS: 10,
  BLANK_TEXT_MAX_LENGTH: 200,

  // Matching
  MIN_PAIRS: 2,
  MAX_PAIRS: 15,
  PAIR_TEXT_MAX_LENGTH: 200,

  // Essay
  RUBRIC_CRITERIA_MIN: 1,
  RUBRIC_CRITERIA_MAX: 10,
  RUBRIC_CRITERIA_NAME_MAX_LENGTH: 200,
  RUBRIC_CRITERIA_DESCRIPTION_MAX_LENGTH: 500,

  // Tags
  TAG_MIN_LENGTH: 2,
  TAG_MAX_LENGTH: 50,
  MAX_TAGS: 10,
} as const

// Exam Limits
export const EXAM_LIMITS = {
  TITLE_MIN_LENGTH: 3,
  TITLE_MAX_LENGTH: 200,
  DESCRIPTION_MAX_LENGTH: 2000,
  INSTRUCTIONS_MAX_LENGTH: 5000,
  MIN_DURATION_MINUTES: 5,
  MAX_DURATION_MINUTES: 480, // 8 hours
  MIN_QUESTIONS: 1,
  MAX_QUESTIONS: 200,
  PASSING_SCORE_MIN: 0,
  PASSING_SCORE_MAX: 100,
} as const

// Classroom Limits
export const CLASSROOM_LIMITS = {
  NAME_MIN_LENGTH: 3,
  NAME_MAX_LENGTH: 200,
  MIN_CAPACITY: 1,
  MAX_CAPACITY: 500,
  CODE_LENGTH: 6,
  LATE_CODE_LENGTH: 8,
  MAX_ACTIVE_CLASSROOMS: 10,
  MAX_LATE_CODES_PER_CLASSROOM: 50,
} as const

// Category Limits
export const CATEGORY_LIMITS = {
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 500,
  COLOR_LENGTH: 7, // Hex color with #
  MAX_CATEGORIES: 50,
} as const

// Subscription Limits
export const SUBSCRIPTION_LIMITS = {
  FREE: {
    MAX_COURSES: 3,
    MAX_QUESTIONS: 100,
    MAX_STUDENTS_PER_CLASSROOM: 30,
    MAX_ACTIVE_CLASSROOMS: 2,
  },
  BASIC: {
    MAX_COURSES: 10,
    MAX_QUESTIONS: 500,
    MAX_STUDENTS_PER_CLASSROOM: 100,
    MAX_ACTIVE_CLASSROOMS: 5,
  },
  PRO: {
    MAX_COURSES: -1, // Unlimited
    MAX_QUESTIONS: -1, // Unlimited
    MAX_STUDENTS_PER_CLASSROOM: 500,
    MAX_ACTIVE_CLASSROOMS: 20,
  },
} as const

// File Upload Limits
export const FILE_LIMITS = {
  IMAGE_MAX_SIZE_MB: 5,
  IMAGE_MAX_SIZE_BYTES: 5 * 1024 * 1024,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  ALLOWED_IMAGE_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
} as const

// Monitoring Limits
export const MONITORING_LIMITS = {
  MAX_EVENTS_PER_STUDENT: 10000,
  EVENT_DATA_MAX_SIZE: 1000, // bytes
  MAX_TAB_SWITCHES: 5,
  MAX_FOCUS_LOSSES: 10,
} as const
